import React, {
	useCallback,
	useMemo,
	useState,
	memo,
} from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import classNames from 'classnames';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import type {
	MediaItem,
	PresentedMediaItem,
} from '../../stores/types/media';
import useMedia from '../../hooks/use-media';
import Portal from '../general-components/portal';
import FeedMediaContextMenu from './feed-media-context-menu';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import { isAudio, isVideo } from '../../utils/utils';
import useShotMemos from '../../hooks/use-shot-memos';
import usePptxDeckAnimations from '../../hooks/use-pptx-deck-animations';
import * as MediaActions from '../../stores/actions/media';
import { PptxDeckPosition, pptxDeckIsAddedPage } from '../../utils/pptx-deck-animations-reconcile';

import MediaVideoSpotlight from '../general-components/media-video-spotlight';
import MediaAudioSpotlight from '../general-components/media-audio-spotlight';
import MediaPresentationFrame from './media-presentation-frame';

interface Props {
	mediaId: number;
	feedPosition: number;
	socket: Socket | null;
	selectedMediaFeed?: number;
	onSelectMediaFeedChange?: React.Dispatch<React.SetStateAction<number | undefined>>;
	isForMainVideoEditingComponent?: boolean;
	iterateLoadedMediaCount: Function;
	isEditingFeed: boolean;
	isForShotEditorModal?: boolean;
	audioOnly?: boolean;
	showbuilderMediaItem?: PresentedMediaItem;
	updateShowbuilderMedia?: Function;
	showBackground?: boolean;
	notVisible?: boolean;
}

export default memo((props: Props) => {
	const {
		mediaId,
		socket,
		isForMainVideoEditingComponent = false,
		iterateLoadedMediaCount,
		isEditingFeed = false,
		feedPosition,
		showbuilderMediaItem,
		updateShowbuilderMedia,
		audioOnly,
	} = props;

	const isForShotEditorModal = props.isForShotEditorModal ? props.isForShotEditorModal : false;
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const modalOpen = useSelector((state: GS) => state.ModalReducer.modalOpen);
	const globalIsDragging = useSelector((state: GS) => state.UserInterfaceReducer.globalIsDragging);

	const {
		currentShot,
	} = useShotMemos();
	const {
		canEditOrControlMedia,
	} = useMedia();

	const [showBackground, setShowBackground] = useState<boolean>(false);
	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();

	const dispatch = useDispatch();

	// used to sync the start time of new videos with paginated media

	// FeedMedia is used for media being presented as well as media in Showbuilder which is why we use
	// both of these variables
	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);

	const isOnCanvas = useMemo(
		() => mediaItem && currentShot.shot_data.shotFeeds.some((feed) => feed.media === mediaId),
		[mediaItem, currentShot.shot_data.shotFeeds],
	);

	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);

	// A DOP build set to play automatically (rather than on click) reports its
	// step with no NextStep from us. Persist it as the new synced position
	// instead of letting the reconciler fight to revert it.
	const handlePptxDeckAutoAdvance = useCallback((pos: PptxDeckPosition) => {
		if (!mediaItem || !presentedMediaItem || !canEditOrControlMedia(mediaItem, user, host, true)) {
			return;
		}
		if (isEditingFeed) {
			updateShowbuilderMedia?.(
				{ ...presentedMediaItem, currentPage: pos.slide, currentStep: pos.step },
			);
		} else {
			dispatch(MediaActions.setPresentedMediaPage(mediaId, pos.slide, undefined, pos.step));
		}
	}, [
		mediaItem,
		presentedMediaItem,
		user,
		host,
		isEditingFeed,
		updateShowbuilderMedia,
		mediaId,
		dispatch,
	]);

	const urlOfCurrentPage = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		const url = imageArray[currentPage - 1] ?? '';
		return url;
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);

	const currentPageIsVideo = useMemo(() => isVideo(urlOfCurrentPage), [urlOfCurrentPage]);
	const currentPageIsAudio = useMemo(() => isAudio(urlOfCurrentPage), [urlOfCurrentPage]);

	const { hasAnimations, contentUrl, slideOrder } = usePptxDeckAnimations(mediaId);
	// A page uploaded onto the deck after conversion renders like a regular media page.
	const showsPageMedia = !hasAnimations
		|| pptxDeckIsAddedPage(slideOrder, presentedMediaItem?.currentPage ?? 1);

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `feed-media-${mediaItem?.id}`, [mediaItem]);

	return (
			<div
				key={feedPosition}
				className={classNames('media', mediaItem?.media_data.fill && 'fill')}
				data-testid='videoMediaControls'
				style={{
					background: showBackground ? mediaItem?.media_data.background_color : '',
				}}
				onContextMenu={(e) => {
					if (host) {
						e.preventDefault();
						setRightClickCoords([e.pageX, e.pageY]);
						dispatch(setOpenContextMenuId(contextMenuId));
					}
				}}
			>
				{
				mediaItem?.id
				&& isOnCanvas
				&& user
				&& (!modalOpen || isForShotEditorModal)
				&& (
					// This portal is to work around a `transform: translateZ(0);` hack on .main-video
					// (the transform makes fixed children elements like the custom context menu fixed
					// relative to it rather than the viewport)
					<Portal targetId="feed-media-context-menu-portal">
						<FeedMediaContextMenu
							mediaItem={mediaItem}
							contextMenuId={contextMenuId}
							clickCoordinates={rightClickCoords}
							socket={socket}
							user={user}
							canEditMedia={canEditOrControlMedia(mediaItem, user, host, false)}
						/>
					</Portal>
				)}
				{
					<div className="flex-center width-full height-full">

						{hasAnimations && contentUrl && (
							<MediaPresentationFrame
								contentUrl={contentUrl}
								mediaId={mediaId}
								currentPage={presentedMediaItem?.currentPage ?? 1}
								currentStep={presentedMediaItem?.currentStep ?? 1}
								onLoad={() => iterateLoadedMediaCount()}
								globalIsDragging={!!globalIsDragging}
								onAutoAdvance={handlePptxDeckAutoAdvance}
							/>
						)}
						{showsPageMedia && !currentPageIsVideo && !currentPageIsAudio && (
							<img
								className="media-img"
								src={urlOfCurrentPage ?? ''}
								alt={''}
								onLoad={() => {
									iterateLoadedMediaCount();
									setShowBackground(true);
								}}
							/>
						)}
						{showsPageMedia && currentPageIsVideo && (
							<MediaVideoSpotlight
								id={`media-video-${mediaId}`}
								key={`${'key_FeedMedia_'}${mediaId}`}
								className="media-video"
								crossOrigin={'anonymous'}
								mediaId={mediaId}
								iterateLoadedMediaCount={iterateLoadedMediaCount}
								socket={socket}
								onCanPlayThrough={() => setShowBackground(true)}
								audioOnly={audioOnly}
								isEditingFeed={isEditingFeed}
								showbuilderMediaItem={showbuilderMediaItem}
								isForMainVideoEditingComponent={isForMainVideoEditingComponent}
								doEchoCancel={true}
								/>
						)}

						{showsPageMedia && currentPageIsAudio && (
							<MediaAudioSpotlight
								id={`media-video-${mediaId}`}
								key={`${'key_FeedMedia_'}${mediaId}`}
								mediaItem={mediaItem}
								loop={!!mediaItem?.media_data.loop}
								iterateLoadedMediaCount={iterateLoadedMediaCount}
								presentedMediaItem={presentedMediaItem}
								socket={socket}
								mediaId={mediaId}
								isPreloading={false}
								isEditingFeed={isEditingFeed}
							/>
						)}
					</div >
				}
			</div>
	);
});
